/**
 * The §14.1 DOCNAME fold, and the uniqueness rule built on it.
 *
 * BMO "identifies, sequences, and de-duplicates our deliveries purely by file name" (spec §14.1),
 * and the DOCNAME field of every delivery name is an originator- or advisor-authored string folded
 * down to `[A-Z0-9-]`. Two documents whose names fold to the same DOCNAME are therefore the SAME
 * document as far as BMO is concerned — `AdditionalDocumentDisposition` resolves the second one to
 * `REPLACE`, and BMO deletes the first.
 *
 * <p>
 * This module is the client-side half of preventing that. {@link foldDocumentName} is a port of
 * `FileXFileName.Field`, and the port has to be exact: the whole point is to compare a name the
 * user is about to author against names already authored, and a fold that disagrees with the
 * server's by one character is a collision we fail to catch — or one we report that isn't real.
 * See the note on {@link foldDocumentName} for why the obvious one-line implementation is wrong.
 * </p>
 */

import type { WorkflowStep } from './templateTypes';

/**
 * One authored document name, folded as BMO will receive it.
 *
 * A branded-ish alias rather than a bare `string` so call sites read unambiguously — the folded
 * form and the typed form are both strings and are easy to mix up, and mixing them up silently
 * disables the check.
 */
export type FoldedDocumentName = string;

/**
 * Folds a document name exactly as `FileXFileName.Field` does: uppercased, every character outside
 * `[A-Z0-9]` replaced by `-`, runs of `-` collapsed, leading and trailing `-` dropped.
 *
 * <p>
 * **Why this is not `value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '-')`.** .NET's
 * `ToUpperInvariant` uses SIMPLE case mapping — one char in, one char out, never an expansion. JS
 * `toUpperCase()` uses FULL case mapping, so `ß` becomes `SS` and the `ﬁ` ligature becomes `FI`,
 * both of which the server leaves alone and then folds away to `-`. The `upper.length === 1` test
 * is what reproduces that refusal to expand.
 * </p>
 *
 * <p>
 * That leaves exactly ONE character where the two still disagree, and it was found by enumerating
 * the whole BMP on both runtimes rather than reasoned about: the characters that are not already
 * ASCII alphanumeric but uppercase INTO a single ASCII alphanumeric are `ſ` (U+017F → `S`) on both
 * sides, plus `ı` (U+0131 → `I`) on JS only — .NET leaves the dotless i untouched. So U+0131 is
 * excluded by hand. Every other non-ASCII character folds to `-` on both runtimes.
 * </p>
 *
 * <p>
 * Iterating code UNITS rather than code points is also deliberate: `Field` iterates .NET `char`,
 * so each half of a surrogate pair folds to its own `-` and the pair then collapses to one. Walking
 * code points would produce the same single `-` here but for a different reason, and the two stop
 * agreeing as soon as an astral character sits beside a dash.
 * </p>
 *
 * @returns the folded name, or `''` when nothing usable survives (an empty or punctuation-only
 * name). Non-throwing because the input is user free text — callers decide what an unusable name
 * means, and they do not agree: the originator has no server-side fallback, while an advisor's
 * unusable name is substituted with `SUPPORTINGDOC`.
 */
/**
 * The one character JS uppercases into ASCII and .NET does not. See {@link foldDocumentName}.
 */
const DOTLESS_I = 'ı';

export function foldDocumentName(value: string): FoldedDocumentName {
  let folded = '';
  let previousWasDash = false;

  for (const ch of value.trim().split('')) {
    const upper = ch.toUpperCase();
    // `length === 1` is what reproduces .NET's refusal to expand a character on uppercasing; the
    // dotless i is the single case where that test is not enough.
    const isDeliverable = upper.length === 1 && /[A-Z0-9]/.test(upper) && ch !== DOTLESS_I;

    if (!isDeliverable) {
      // Leading dashes are DROPPED, not collapsed — `Field` guards its append with
      // `collapsed.Length > 0`, so "---Void" folds to "VOID" and not "-VOID".
      previousWasDash = true;
      continue;
    }

    if (previousWasDash && folded.length > 0) folded += '-';
    previousWasDash = false;
    folded += upper;
  }

  return folded;
}

/**
 * Forces a generated-PDF label to end with `.pdf`.
 *
 * <p>
 * Lives here rather than in the PDF drawer that owns the input because the suffix is part of the
 * delivered DOCNAME — `BmoDataBuilder.DeliveryNameFor` passes the stored label straight through, so
 * `Void Cheque.pdf` is delivered as `VOID-CHEQUE-PDF`. Anything comparing authored names has to
 * apply the same suffix the drawer applies on save, and a second copy of that rule would drift.
 * </p>
 */
export function ensurePdfSuffix(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  return /\.pdf$/i.test(trimmed) ? trimmed : `${trimmed}.pdf`;
}

/**
 * The DOCNAME a generated-PDF entry will be delivered under.
 *
 * Exported separately from {@link collectAuthoredDocumentNames} so a caller validating a name the
 * originator is still typing folds it the same way the peer set was folded. Validating the raw
 * draft instead would read `Void Cheque` as free while a peer holds `Void Cheque.pdf`, and then
 * save exactly that collision.
 */
export function foldPdfEntryLabel(label: string): FoldedDocumentName {
  return foldDocumentName(ensurePdfSuffix(label));
}

/**
 * Every DOCNAME the originator has already authored on this workflow, mapped back to the label
 * that produced it so a message can name the peer that clashed.
 *
 * <p>
 * Spans BOTH authored lists — generated PDFs and additional-document slots — in one namespace.
 * That is stricter than delivery strictly requires: a generated PDF is minted as a `PACKAGE` name
 * and an upload as a `SUPPORTING` one, and `TryParseSupportingDocument` refuses a `PACKAGE` name,
 * so the two can never supersede each other. The flat rule is still the right one, because the
 * alternative is scoping by obligation — and obligation is a TOGGLE (`isRequired`) whose handler
 * has no error surface, so flipping it would retroactively create a collision reported nowhere.
 * One rule an originator can state in a sentence beats a correct one they cannot predict.
 * </p>
 *
 * <p>
 * Reads the entry lists off every step rather than filtering by step type, the same data-keyed
 * approach `stepsWithPdfTemplates` takes and for the same reason — legacy `PdfFileUpload` rows are
 * folded onto `PdfGeneration` on read, and a type test drifts with that rename.
 * </p>
 *
 * <p>
 * Conditions are deliberately ignored. They are evaluated against advisor answers that do not
 * exist at authoring time, so two entries gated on mutually exclusive conditions are provably never
 * co-delivered — but the drawer cannot prove it, and a rule the originator cannot predict is worse
 * than one that over-reserves three names.
 * </p>
 *
 * @param opts.excludeEntryId the entry being edited, so a name never collides with itself.
 */
export function collectAuthoredDocumentNames(
  steps: readonly WorkflowStep[],
  opts: { excludeEntryId?: string } = {},
): Map<FoldedDocumentName, string> {
  const { excludeEntryId } = opts;
  const taken = new Map<FoldedDocumentName, string>();

  const claim = (
    entryId: string,
    label: string | undefined | null,
    normalize: (label: string) => string,
  ) => {
    if (entryId === excludeEntryId) return;
    // A row with no label at all reserves nothing. This runs on every render of the advisor's
    // document step, so a partial entry — a stale bundle meeting a newer payload, a fixture that
    // sets only the fields its own assertion needs — must not take the step down with it.
    if (!label) return;

    const normalized = normalize(label);
    const folded = foldDocumentName(normalized);
    // An unusable name is not a name. Two slots called "???" are not duplicates of each other, and
    // saying they are would quote back a string nobody typed.
    if (folded === '') return;
    // First writer wins, so the message names the entry that held the name first.
    if (!taken.has(folded)) taken.set(folded, normalized);
  };

  for (const step of steps) {
    for (const entry of step.fileEntries ?? []) {
      claim(entry.id, entry.label, ensurePdfSuffix);
    }
    for (const entry of step.additionalDocumentEntries ?? []) {
      claim(entry.id, entry.label, (label) => label.trim());
    }
  }

  return taken;
}

/**
 * A default label with ` 2`, ` 3`, … appended until its fold is free.
 *
 * <p>
 * The "Add PDF" / "Add Document" buttons POST a constant localized default, so clicking either
 * twice would author an instant duplicate and the save would be refused — a rejection on the single
 * most common authoring action, for a name the originator never chose. Suffixing is applied to the
 * STEM so `PDF Name.pdf` becomes `PDF Name 2.pdf` rather than `PDF Name.pdf 2`.
 * </p>
 */
export function uniqueDefaultDocumentLabel(
  defaultLabel: string,
  taken: ReadonlyMap<FoldedDocumentName, string>,
  fold: (label: string) => FoldedDocumentName = foldDocumentName,
): string {
  if (!taken.has(fold(defaultLabel))) return defaultLabel;

  const dot = defaultLabel.lastIndexOf('.');
  const stem = dot > 0 ? defaultLabel.slice(0, dot) : defaultLabel;
  const extension = dot > 0 ? defaultLabel.slice(dot) : '';

  // Bounded by the number of peers plus one: each iteration can collide with at most one distinct
  // taken name, so a free candidate is always reached before the bound.
  for (let n = 2; n <= taken.size + 2; n += 1) {
    const candidate = `${stem} ${n}${extension}`;
    if (!taken.has(fold(candidate))) return candidate;
  }

  return defaultLabel;
}
